import { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './Sidebar.module.css';
import PushPinIcon from '@mui/icons-material/PushPin';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import ConfirmationBox from '../DialogBoxes/ConfirmationBox/ConfirmationBox';
import AppLink from '../AppLink/AppLink';
import Tooltip from '../Tooltip/Tooltip';
import getCMResponse from '../../utils/api_manager';
import { buildProjectPath } from '../../utils/projectPath';

// sidebar-nav-revamp/trd.md R4/R7: pinned row = [title (ellipsis)] [20px pin
// column]; full name shown in a portal tooltip only when the title truncates.
const PinnedRow = ({ dashboard, to, onNavigate, onUnpinClick, sidebarRef }) => {
  const titleRef = useRef(null);
  return (
    <Tooltip
      portal
      placement="right"
      offset={8}
      maxWidth="280px"
      display="block"
      anchorRef={sidebarRef}
      truncationRef={titleRef}
      triggerOnClick={false}
      content={dashboard.name}
    >
      <AppLink
        className={`${styles.navItem} ${styles.pinnedItem}`}
        to={to}
        onNavigate={onNavigate}
      >
        <span className={styles.pinnedTitle} ref={titleRef}>{dashboard.name}</span>
        <span className={styles.pinColumn}>
          <PushPinIcon
            className={styles.pinIcon}
            onClick={(e) => onUnpinClick(e, dashboard)}
            titleAccess="Unpin dashboard"
          />
        </span>
      </AppLink>
    </Tooltip>
  );
};

const Sidebar = ({ currentProject, dashboards, refreshDashboards, isCollapsed, setIsCollapsed, isMobileOpen, onNavigate }) => {
  const location = useLocation();
  const [unpinTarget, setUnpinTarget] = useState(null);
  const sidebarRef = useRef(null);

  const pinnedDashboards = (dashboards || []).filter(d => d.is_pinned);

  // dashboard-suite-dark-theme-revamp/trd.md Section 2.4: apply the same
  // location.pathname active-state pattern already used for Settings to
  // Insights and All Dashboards, (active
  // whenever the current route is any dashboard route — the "All
  // Dashboards" list, or an individual pinned dashboard's detail route).
  const insightsPath = buildProjectPath(currentProject?.id, '/analyze/insights');
  const allDashboardsPath = buildProjectPath(currentProject?.id, '/analyze/dashboards/all');
  const dashboardDetailPrefix = buildProjectPath(currentProject?.id, '/analyze/dashboard/');
  const isDashboardsActive = location.pathname === allDashboardsPath
    || location.pathname.startsWith(dashboardDetailPrefix);

  // visibility-planner TRD 4.1: SHOPIFY-only, last child of <nav>.
  const visibilityPlannerPath = buildProjectPath(currentProject?.id, '/visibility-planner');
  const showVisibilityPlanner = currentProject?.project_type === 'SHOPIFY';

  const handlePinIconClick = useCallback((event, dashboard) => {
    event.preventDefault();
    event.stopPropagation();
    setUnpinTarget(dashboard);
  }, []);

  const handleCancelUnpin = useCallback(() => {
    setUnpinTarget(null);
  }, []);

  const handleConfirmUnpin = useCallback(() => {
    if (!unpinTarget) return;
    const dashboardId = unpinTarget.id;
    setUnpinTarget(null);

    getCMResponse(`/analyze/${dashboardId}/unpin`, 'POST')
      .then(response => {
        if (response.success) {
          if (refreshDashboards && currentProject?.id) {
            refreshDashboards(currentProject.id);
          }
        } else {
          console.error('Failed to unpin dashboard:', response);
        }
      })
      .catch(error => {
        console.error('Error unpinning dashboard:', error);
      });
  }, [unpinTarget, refreshDashboards, currentProject]);

  return (
    <aside ref={sidebarRef} className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ''} ${isMobileOpen ? styles.mobileOpen : ''}`}>
      <nav className={styles.sidebarNav}>
        <AppLink
          className={`${styles.navItem} ${location.pathname === insightsPath ? styles.activeNavItem : ''}`}
          to={insightsPath}
          onNavigate={onNavigate}
        >
          <LightbulbIcon />
          <span>Insights</span>
        </AppLink>

        <AppLink
          className={`${styles.navItem} ${isDashboardsActive ? styles.activeNavItem : ''}`}
          to={allDashboardsPath}
          onNavigate={onNavigate}
        >
          <SpaceDashboardIcon />
          <span>All Dashboards</span>
        </AppLink>

        {pinnedDashboards.length > 0 && (
          <div className={styles.pinnedSection}>
            {pinnedDashboards.map(dashboard => (
              <PinnedRow
                key={dashboard.id}
                dashboard={dashboard}
                to={buildProjectPath(currentProject?.id, `/analyze/dashboard/${dashboard.id}`)}
                onNavigate={onNavigate}
                onUnpinClick={handlePinIconClick}
                sidebarRef={sidebarRef}
              />
            ))}
          </div>
        )}

        {showVisibilityPlanner && (
          <Tooltip
            portal
            placement="right"
            offset={8}
            maxWidth="280px"
            display="block"
            anchorRef={sidebarRef}
            triggerOnClick={false}
            disabled={!isCollapsed}
            content="Visibility Planner"
          >
            <AppLink
              className={`${styles.navItem} ${location.pathname === visibilityPlannerPath ? styles.activeNavItem : ''}`}
              to={visibilityPlannerPath}
              onNavigate={onNavigate}
            >
              <LeaderboardIcon />
              <span>Visibility Planner</span>
            </AppLink>
          </Tooltip>
        )}
      </nav>

      <div className={styles.sidebarFooter}>
        <AppLink
          className={`${styles.navItem} ${location.pathname === buildProjectPath(currentProject?.id, '/settings') ? styles.activeNavItem : ''}`}
          to={buildProjectPath(currentProject?.id, '/settings')}
          onNavigate={onNavigate}
        >
          <SettingsIcon />
          <span>Settings</span>
        </AppLink>
        <button
          className={styles.collapseButton}
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronRightIcon className={isCollapsed ? '' : styles.rotated} />
          <span className={styles.collapseText}>Collapse Sidebar</span>
        </button>
      </div>

      <ConfirmationBox
        isVisible={!!unpinTarget}
        title="Unpin dashboard"
        message={unpinTarget ? `Unpin "${unpinTarget.name}" from your sidebar?` : ''}
        confirmText="Unpin"
        onConfirm={handleConfirmUnpin}
        onCancel={handleCancelUnpin}
      />
    </aside>
  );
};

export default Sidebar;
